import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { getConsultation } from '../../../virtco/services/data-provider';
import { logout } from '../../../redux/action/logout-action';
import { getAllCategoriesMaster } from '../../../virtco/services/data-provider';
import { getSubCategoryByCategoryId } from '../../../virtco/services/data-provider';
import { getSkillsByCategoryId } from '../../../virtco/services/data-provider';

interface CategorizationState {
  id: string | null;
  category: string | null;
  subCategory: string | null;
  skills: string[];
  categoryOptions: { label: string; value: string }[];
  subCategoryOptions: { label: string; value: string }[];
  skillsOptions: { label: string; value: string }[];
  loadingCategories: boolean;
  loadingSubCategories: boolean;
  loadingSkills: boolean;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  // Tracks the consultationId we've successfully fetched categorization for.
  // Used to skip refetching on step-navigation remounts so that locally-typed
  // (unsaved) edits don't get overwritten by the API response.
  fetchedForId: string | null;
}

const initialState: CategorizationState = {
  id: null,
  category: null,
  subCategory: null,
  skills: [],
  categoryOptions: [],
  subCategoryOptions: [],
  skillsOptions: [],
  loadingCategories: false,
  loadingSubCategories: false,
  loadingSkills: false,
  status: 'idle',
  fetchedForId: null,
};

export const fetchCategorization = createAsyncThunk(
  'categorization/fetchCategorization',
  async (consultationId: string) => {
    const res = await getConsultation(consultationId);
    const data = res.data?.data;
    const tagTitles = (data?.consultationServiceTags || []).map((tagObj: any) => tagObj.tag.title);
    return {
      id: data?.id || null,
      category: data?.categoryId || null,
      subCategory: data?.subCategoryId || null,
      skills: tagTitles,
      categoryOptions: [],
      subCategoryOptions: [],
      skillsOptions: [],
    };
  }
);

// Async thunks for fetching options
export const fetchCategories = createAsyncThunk(
  'categorization/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const categoriesRes = await getAllCategoriesMaster();
      const option1 = categoriesRes?.data?.data;
      const transformedOptions = option1?.map((item: any) => ({
        label: item.name,
        value: item.id
      })) || [];
      return transformedOptions;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchSubCategories = createAsyncThunk(
  'categorization/fetchSubCategories',
  async (categoryId: string, { rejectWithValue }) => {
    try {
      const res: any = await getSubCategoryByCategoryId(categoryId);
      if (res.status) {
        const transformedSubOptions = res?.data?.data?.map((item: any) => ({
          label: item.name,
          value: item.id
        })) || [];
        return transformedSubOptions;
      } else {
        return rejectWithValue(res.data.message);
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const fetchSkills = createAsyncThunk(
  'categorization/fetchSkills',
  async (categoryId: string, { rejectWithValue }) => {
    try {
      const res: any = await getSkillsByCategoryId(String(categoryId));
      if (res && res.data && Array.isArray(res.data.data)) {
        const transformedSkillsOptions = res.data.data.map((item: any) => ({
          label: item.name,
          value: item.id
        }));
        return transformedSkillsOptions;
      } else {
        return [];
      }
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const categorizationSlice = createSlice({
  name: 'categorization',
  initialState,
  reducers: {
    setCategorization: (state, action: PayloadAction<Partial<CategorizationState>>) => {
      return { ...state, ...action.payload };
    },
    setData(state, action: PayloadAction<{ id?: string; category: string | null; subCategory: string | null; skills: string[]; status?: string }>) {
      state.id = action.payload.id || null;
      state.category = action.payload.category;
      state.subCategory = action.payload.subCategory;
      state.skills = action.payload.skills;
      if (action.payload.status) {
        state.status = action.payload.status as any;
      }
    },
    setCategory(state, action: PayloadAction<string | null>) {
      state.category = action.payload;
      state.subCategory = null;
      state.subCategoryOptions = [];
      state.skills = [];
      state.skillsOptions = [];
    },
    setSubCategory(state, action: PayloadAction<string | null>) {
      state.subCategory = action.payload;
    },
    setSkills(state, action: PayloadAction<string[]>) {
      state.skills = action.payload;
    },
    clearData(state) {
      state.id = null;
      state.category = null;
      state.subCategory = null;
      state.skills = [];
      state.categoryOptions = [];
      state.subCategoryOptions = [];
      state.skillsOptions = [];
      state.loadingCategories = false;
      state.loadingSubCategories = false;
      state.loadingSkills = false;
      state.status = 'idle';
      state.fetchedForId = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCategorization.pending, (state, action) => {
        if (state.fetchedForId !== action.meta.arg) {
          state.id = null;
          state.category = null;
          state.subCategory = null;
          state.skills = [];
          state.subCategoryOptions = [];
          state.skillsOptions = [];
          state.fetchedForId = null;
        }
        state.status = 'loading';
      })
      .addCase(fetchCategorization.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.id = action.payload.id;
        state.category = action.payload.category;
        state.subCategory = action.payload.subCategory;
        state.skills = action.payload.skills;
        state.fetchedForId = action.meta.arg;
        // options will be loaded separately
      })
      .addCase(fetchCategorization.rejected, state => {
        state.status = 'failed';
      })
      .addCase(fetchCategories.pending, state => {
        state.loadingCategories = true;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loadingCategories = false;
        state.categoryOptions = action.payload;
      })
      .addCase(fetchCategories.rejected, state => {
        state.loadingCategories = false;
      })
      .addCase(fetchSubCategories.pending, state => {
        state.loadingSubCategories = true;
      })
      .addCase(fetchSubCategories.fulfilled, (state, action) => {
        state.loadingSubCategories = false;
        state.subCategoryOptions = action.payload;
      })
      .addCase(fetchSubCategories.rejected, state => {
        state.loadingSubCategories = false;
        state.subCategoryOptions = [];
      })
      .addCase(fetchSkills.pending, state => {
        state.loadingSkills = true;
      })
      .addCase(fetchSkills.fulfilled, (state, action) => {
        state.loadingSkills = false;
        state.skillsOptions = action.payload;
      })
      .addCase(fetchSkills.rejected, state => {
        state.loadingSkills = false;
        state.skillsOptions = [];
      })
      .addCase(logout, () => initialState);
  },
});

export const { setCategorization, setData, setCategory, setSubCategory, setSkills, clearData } = categorizationSlice.actions;
export default categorizationSlice.reducer;

// Selector for consultationId and status
export const selectCategorizationConsultationId = (state: any) => state.categorization.id;
export const selectCategorizationStatus = (state: any) => state.categorization.status; 